// ==========================================
// 1. src/components/sections/AboutSection.js
// ==========================================
import React, { useState } from 'react';
import { Target, Heart, Lightbulb, Award } from 'lucide-react';
import SectionHeader from '../common/SectionHeader';

const ValueCard = ({ icon: Icon, title, description }) => (
  <div className="text-center">
    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <Icon className="w-8 h-8 text-blue-600" />
    </div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

const AboutSection = () => {
  const [expanded, setExpanded] = useState(false);

  const values = [
    {
      icon: Target,
      title: 'Mission-Driven',
      description: 'We exist to eliminate the friction that keeps logistics companies from reaching their full potential.'
    },
    {
      icon: Heart,
      title: 'Operator-First',
      description: 'Every decision we make starts with one question: Does this make life easier for dispatchers and drivers?'
    },
    {
      icon: Lightbulb,
      title: 'Innovation with Purpose',
      description: 'We innovate not for the sake of technology, but to solve real problems that cost real money.'
    },
    {
      icon: Award,
      title: 'Proven Excellence',
      description: 'Our track record speaks for itself—from USPS contracts to regional carriers, we deliver results.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          title="About SYBR"
          subtitle="Born from frustration, built with experience, driven by results"
        />

        {/* Story Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Story</h3>

            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p className="text-lg font-semibold text-gray-900">
                SYBR was founded on a vision: build the impossible.
              </p>
              <p>
                For decades, freight companies have been told that AI can't handle the chaos of real-world logistics.
                Too many variables. Too much nuance. Too unpredictable.
                The technology wasn't there, and the expertise didn't exist to bridge engineering precision with operational reality.
              </p>
              <p>
                Our CEO and Founder,{" "}
                <a
                  href="https://www.linkedin.com/in/themikeysherm/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Michael Shermot
                </a>
                , saw something different.
              </p>


              {/* Read More toggle section */}
              {!expanded && (
                <button
                  onClick={() => setExpanded(true)}
                  className="text-blue-600 font-medium underline hover:text-blue-800 transition"
                >
                  Read More
                </button>
              )}

              <div
                className={`transition-all duration-500 ease-in-out overflow-hidden ${expanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                  }`}
              >
                <p>
                  He started his career in Naval Architecture and Marine Engineering—where precision was the difference between floating and sinking.
                  When he moved into Computer Science, he found his edge: the ability to blend engineering rigor with creative problem-solving.
                  Engineering solutions from chaos.
                </p>
                <br />
                <p>
                  While others doubted whether logistics AI was even possible, Michael knew the real question wasn't if—it was how.
                  He came to the realization: <span className="font-medium text-gray-900">
                    "I can hit targets no one else can see."
                  </span>
                </p>
                <br />
                <p>
                  In 2022, he made it his mission.
                  He left his job to pursue SYBR full-time, bootstrapping development and iterating relentlessly until the platform found product-market fit.
                </p>
                <br />
                <p>
                  By 2023, it worked. The first customer signed on. SYBR gained momentum.
                </p>
                <br />
                <p>
                  July 2024: SYBR expanded nationwide. Within months, the platform was processing operations at scale while maintaining the reliability that freight operations demand.
                </p>
                <div className="pl-4 border-l-4 border-blue-500 my-8">
                  <p className="font-medium text-gray-900 text-lg">
                    Today, SYBR powers fleets of every size, processing millions of transactions monthly and eliminating thousands of hours of manual work.
                  </p>
                </div>

                <p>
                  We're not just another software company. We're engineers who live the grind, understand the chaos, and build the impossible.
                  <div className="pl-4 border-l-4 border-blue-500 my-8">
                    <p className="font-medium text-gray-900 text-lg">
                      When your business is on the line, SYBR works.
                    </p>
                  </div>
                </p>

                <button
                  onClick={() => setExpanded(false)}
                  className="text-blue-600 font-medium underline hover:text-blue-800 transition"
                >
                  Read Less
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {values.map((value, index) => (
            <ValueCard key={index} {...value} />
          ))}
        </div>

      </div>
    </section>
  );
};

export default AboutSection;

